
import * as VerifyCodeFormStyles from './VerifyCodeForm.styles';

import * as authStyles from '../../Auth.styles';
import CodeVerificationNotice from '../../../CodeVerificationNotice';
import FormTextField from '../../../FormTextField';
import FormikWrapper from '../../../FormikWrapper';
import ProceedButton from '../../../ProceedButton';
import authFormLabel from '../../utils/authFormLabel';
import { AccountField, verificationCodeRegExp } from '../../../../utils/forms';

import {
  AuthStateContext,
  AuthStepAction,
  initialAuthContext,
} from '../../../../utils/authFormState';
import { PrivateRoutePath, PublicRoutePath } from '../../../../utils/routes';
import { createSessionPerform } from '../../../../store/session/actions';
import { submittingAuth$ } from '../../../../store/submitting/selectors';

import { useRouter } from 'next/router';
import { useDispatch, useSelector } from 'react-redux';
import { useContext } from 'react';
import { Form, useField } from 'formik';
import * as yup from 'yup';
import * as R from 'ramda';
import type {
  AuthContextFields} from '../../../../utils/authFormState';
import type { SignUpFormData} from '../../../../utils/forms';
import type { Profile } from 'foundshared/src/api/profile/types';

const validationSchema = yup.object().shape({
  verificationCode: yup
    .string()
    .min(6, 'Verification code is at least 6 digits long')
    .matches(verificationCodeRegExp, 'Verification code is not valid')
    .required('Please enter the verification code'),
});

type AuthFields = AuthContextFields | SignUpFormData;

export interface VerifyCodeFormProps {
  /** Redirect route after verification complete. */
  redirect?: string;
  /** If this component is used as part of a sign-up (inside a <form />) or as sign-in (login form). */
  embed?: boolean;
  /** Should the component NOT render a resend button and verification message? */
  noResend?: boolean;
}

interface ContentsProps extends Omit<VerifyCodeFormProps, 'redirect'> {
  handleOnSubmit?: (values: AuthFields) => void;
}

function Contents({ noResend, embed, handleOnSubmit }: ContentsProps) {
  const submitting = useSelector(submittingAuth$);
  const { send } = useContext(AuthStateContext);

  const [{ value: mobileNumber }] = useField<AuthFields[AccountField.MOBILE]>(AccountField.MOBILE);

  const [{ value: code }, { error }, { setValue, setTouched }] = useField<AuthFields[AccountField.VERIFICATION_CODE]>(
    AccountField.VERIFICATION_CODE
  );

  const { classes: buttonClasses } = authStyles.useButton();

  const handleRequestCodeAgain = () => {
    setTouched(false);
    setValue('');
    send(AuthStepAction.PREVIOUS);
  };

  const isDisabled = !!error || !mobileNumber || submitting;

  const submit = () => {
    if (!!handleOnSubmit && mobileNumber && code) {
      handleOnSubmit({
        [AccountField.MOBILE]: mobileNumber,
        [AccountField.VERIFICATION_CODE]: code,
      });
    }
  };

  return (
    <>
      {!noResend && <CodeVerificationNotice resend={() => handleRequestCodeAgain()} phoneNumber={mobileNumber} />}

      <FormTextField
        fieldId={AccountField.VERIFICATION_CODE}
        fullWidth
        autoFocus
        type="number"
        label={authFormLabel[AccountField.VERIFICATION_CODE]}
        inputProps={{ autoComplete: 'off' }}
        disabled={submitting}
      />

      {!embed && (
        <ProceedButton
          classes={buttonClasses}
          fullWidth
          loading={submitting}
          disabled={isDisabled}
          type={embed ? 'button' : 'submit'}
          {...(embed ? { onClick: submit } : {})}
        >
          Submit
        </ProceedButton>
      )}
    </>
  );
}

function VerifyCodeForm({ redirect, embed, noResend }: VerifyCodeFormProps) {
  const { classes } = VerifyCodeFormStyles.useStyles();

  const { current, send } = useContext(AuthStateContext);
  const dispatch = useDispatch();
  const { push } = useRouter();

  const initialValues = R.pathOr(initialAuthContext.data, ['context', 'data'], current);

  // if we have redirect then redux will handle this on success
  const successRedirect = redirect
    ? {
        profileCallback: ({ customer }: Profile) => {
          if (redirect === PrivateRoutePath.REFERRALS && !customer.referralEligible) {
            void push(PublicRoutePath.HOME);
          } else {
            void push(redirect);
          }
        },
      }
    : undefined;

  // pass the values to the machine and dispatch the verify action to redux
  const handleOnSubmit = (values: AuthContextFields) => {
    send(AuthStepAction.SUBMIT, {
      data: values,
      callback: () => dispatch(createSessionPerform(values, successRedirect)),
    });
  };

  return (
    <div className={classes.root}>
      {embed ? (
        <Contents embed={embed} noResend={noResend} />
      ) : (
        <FormikWrapper
          initialValues={initialValues}
          validateOnBlur={false}
          validateOnMount={false}
          validationSchema={validationSchema}
          onSubmit={handleOnSubmit}
        >
          <Form>
            <Contents />
          </Form>
        </FormikWrapper>
      )}
    </div>
  );
}

export default VerifyCodeForm;
